/**
 * Parses <Rsrc> tags in message content and converts them to a format
 * suitable for markdown rendering
 */
export const replaceTagsWithNumbers = (html: string): string => {
  const regex = /<Rsrc([^>]*)\/>/g;
  const parts: string[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(html)) !== null) {
    const fullMatch = match[0];
    const offset = match.index;
    const attributes = match[1];

    const numTypeMatch = attributes.match(/num_type="([^"]+)"/);
    const numMatch = attributes.match(/num="([^"]+)"/);
    const rsrcTypeMatch = attributes.match(/rsrc_type="([^"]+)"/);
    const rsrcIdMatch = attributes.match(/rsrc_id="([^"]+)"/);

    if (numTypeMatch && numMatch && rsrcTypeMatch && rsrcIdMatch) {
      const numType = numTypeMatch[1];
      const num = numMatch[1];
      const rsrcType = rsrcTypeMatch[1];
      const rsrcId = rsrcIdMatch[1];

      // For patent/publication paragraphs, preserve leading zeros for proper ID matching
      let cleanNum: string;
      if ((rsrcType === 'patent' || rsrcType === 'publication') && numType === 'paragraph') {
        cleanNum = num.replace(/^p-/, ''); // Remove 'p-' prefix but keep leading zeros
      } else {
        cleanNum = num.replace(/^p-0*/, '').replace(/^0+/, ''); // Original behavior for other types
      }

      // Skip upload resource types entirely (remove tag and leading spaces)
      if (rsrcType === 'upload') {
        const textBeforeMatch = html.substring(lastIndex, offset);
        const trimmedText = textBeforeMatch.replace(/\s+$/, '');
        parts.push(trimmedText);
        lastIndex = offset + fullMatch.length;
      }
      // Skip undefined or empty values and don't add spaces
      else if (!cleanNum || cleanNum === 'undefined' || !numType || numType === 'undefined') {
        const textBeforeMatch = html.substring(lastIndex, offset);
        const trimmedText = textBeforeMatch.replace(/\s+$/, '');
        parts.push(trimmedText);
        lastIndex = offset + fullMatch.length;
      } else {
        parts.push(html.substring(lastIndex, offset));
        // Create a span with data attributes for the click handler
        parts.push(
          `<span class="rsrc-link" data-num-type="${numType}" data-clean-num="${cleanNum}" data-rsrc-type="${rsrcType}" data-rsrc-id="${rsrcId}">${cleanNum}</span>`
        );
        lastIndex = offset + fullMatch.length;
      }
    }
  }

  parts.push(html.substring(lastIndex));
  return parts.join('');
};

/**
 * Convert resource type to proper title case
 */
const toTitleCase = (str: string): string => {
  if (!str) return str;

  // Keep certain acronyms in all caps
  const upperCaseTypes = ['cfr', 'mpep', 'usc', 'epo', 'wipo', 'uspto'];
  const lowerStr = str.toLowerCase();

  if (upperCaseTypes.includes(lowerStr)) {
    return str.toUpperCase();
  }

  // Otherwise use title case (capitalize first letter)
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Format resource tag into display text
 */
export const formatResourceDisplay = (
  rsrcType: string,
  rsrcId: string,
  cleanNum: string,
  numType: string
): string => {
  if (rsrcType === 'cfr') {
    return `37 CFR ${rsrcId}`;
  } else if (rsrcType === 'mpep') {
    return `MPEP ${rsrcId}`;
  } else if (rsrcType === 'usc') {
    return `35 USC ${rsrcId}`;
  } else if (rsrcType === 'patent' && numType === 'paragraph') {
    return `Patent ${rsrcId} ¶${cleanNum}`;
  } else if (rsrcType === 'publication' && numType === 'paragraph') {
    return `Publication ${rsrcId} ¶${cleanNum}`;
  } else {
    return `${toTitleCase(rsrcType)} ${rsrcId}`;
  }
};

/**
 * Strips <Rsrc> tags from content and replaces them with full formatted resource display
 * Used when copying text to clipboard
 */
export const stripResourceTags = (html: string): string => {
  const regex = /<Rsrc([^>]*)\/>/g;
  const parts: string[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(html)) !== null) {
    const fullMatch = match[0];
    const offset = match.index;
    const attributes = match[1];

    const numTypeMatch = attributes.match(/num_type="([^"]+)"/);
    const numMatch = attributes.match(/num="([^"]+)"/);
    const rsrcTypeMatch = attributes.match(/rsrc_type="([^"]+)"/);
    const rsrcIdMatch = attributes.match(/rsrc_id="([^"]+)"/);

    if (numTypeMatch && numMatch && rsrcTypeMatch && rsrcIdMatch) {
      const numType = numTypeMatch[1];
      const num = numMatch[1];
      const rsrcType = rsrcTypeMatch[1];
      const rsrcId = rsrcIdMatch[1];

      // For patent/publication paragraphs, preserve leading zeros
      let cleanNum: string;
      if ((rsrcType === 'patent' || rsrcType === 'publication') && numType === 'paragraph') {
        cleanNum = num.replace(/^p-/, '');
      } else {
        cleanNum = num.replace(/^p-0*/, '').replace(/^0+/, '');
      }

      // Skip upload resource types (remove tag and trailing spaces)
      if (rsrcType === 'upload') {
        const textBeforeMatch = html.substring(lastIndex, offset);
        const trimmedText = textBeforeMatch.replace(/\s+$/, '');
        parts.push(trimmedText);
        lastIndex = offset + fullMatch.length;
      }
      // Skip undefined or empty values
      else if (!cleanNum || cleanNum === 'undefined' || !numType || numType === 'undefined') {
        const textBeforeMatch = html.substring(lastIndex, offset);
        const trimmedText = textBeforeMatch.replace(/\s+$/, '');
        parts.push(trimmedText);
        lastIndex = offset + fullMatch.length;
      } else {
        // Add text before the tag and the full formatted resource display
        parts.push(html.substring(lastIndex, offset));
        const displayText = formatResourceDisplay(rsrcType, rsrcId, cleanNum, numType);
        parts.push(displayText);
        lastIndex = offset + fullMatch.length;
      }
    } else {
      // If tag is malformed, skip it entirely
      const textBeforeMatch = html.substring(lastIndex, offset);
      const trimmedText = textBeforeMatch.replace(/\s+$/, '');
      parts.push(trimmedText);
      lastIndex = offset + fullMatch.length;
    }
  }

  parts.push(html.substring(lastIndex));
  return parts.join('');
};
